/**
 * Feature Flag Service
 * Fetches and manages feature flags from backend
 */

import apiClient from './apiClient';

// Cached under a flat (non-per-user) key -- a browser tab is normally
// single-user at a time, and the first successful fetch after a different
// user logs in overwrites it immediately, so the only edge case this
// trades away is a moment of a previous user's flags surviving a network
// failure that happens to land exactly on a fresh login. Acceptable next
// to what it fixes: a plain network blip (confirmed real right after
// onboarding, when the backend can still be cold/settling) used to wipe
// every feature flag to `{}` with no fallback at all -- every hasFeature()
// check across the app would report false, disabling AI/chat/etc.
// entirely until the next successful reload.
const CACHE_KEY = 'tarr_feature_flags_cache';

// Mirrors server/routes/auth.js's complete-onboarding contentFeatureByType --
// the only mapping the client needs to translate onboarding answers into the
// same feature keys the server would eventually echo back via a real fetch.
const CONTENT_FEATURE_BY_TYPE = {
  screenplay: 'content_screenplay',
  production: 'content_production',
  book: 'content_books',
  shortform: 'content_short_form',
};

class FeatureFlagService {
  constructor() {
    this.features = {};
    this.customLimits = {};
    this.isLoaded = false;
  }

  _loadFromCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return { features: parsed.features || {}, customLimits: parsed.customLimits || {} };
    } catch (e) {
      return null;
    }
  }

  _saveToCache(features, customLimits) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ features, customLimits, cachedAt: Date.now() }));
    } catch (e) {
      // Storage full/unavailable (private browsing, etc.) -- caching is a
      // best-effort resilience layer, not a hard requirement.
    }
  }

  /**
   * Fetch user's features from backend. One quick retry on failure before
   * falling back to cache -- covers the common "brief moment" case (e.g.
   * right after onboarding, when the backend can still be cold/settling)
   * where the very next request would have succeeded, without needing a
   * cache at all.
   */
  async fetchUserFeatures(_isRetry = false) {
    try {
      const response = await apiClient.get('/api/preferences/features');

      if (response?.success) {
        this.features = response.features || {};
        this.customLimits = response.customLimits || {};
        this.isLoaded = true;
        this._saveToCache(this.features, this.customLimits);

        console.log('[FeatureFlags] Loaded features:', this.features);
        return { features: this.features, customLimits: this.customLimits };
      }

      return { features: {}, customLimits: {} };
    } catch (error) {
      if (!_isRetry) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return this.fetchUserFeatures(true);
      }

      console.error('[FeatureFlags] Failed to fetch features:', error);

      // Fall back to the last successfully-loaded set instead of wiping
      // everything -- only a genuine first-ever load with no prior success
      // (e.g. right after onboarding, before any fetch has ever succeeded)
      // has no cache to fall back to, in which case {} is the honest answer.
      const cached = this._loadFromCache();
      this.features = cached?.features || {};
      this.customLimits = cached?.customLimits || {};
      this.isLoaded = true;
      if (cached) {
        console.warn('[FeatureFlags] Using cached features from last successful load');
      }

      return { features: this.features, customLimits: this.customLimits };
    }
  }

  /**
   * Seed the in-memory + cached flags directly from onboarding answers,
   * instead of waiting on a round-trip to /api/preferences/features right
   * after the server just wrote those same answers as overrides. Covers the
   * exact "brief moment after onboarding" gap: content-type/AI toggles the
   * user just picked should apply immediately, not read as disabled because
   * the follow-up fetch hasn't landed yet (or hit a cold/settling backend).
   *
   * Merges onto whatever is already loaded so unrelated flags (plan-level
   * features not touched by onboarding) are left as they were.
   */
  seedFromOnboarding({ selectedContentTypes } = {}) {
    const seeded = { ...this.features };

    if (Array.isArray(selectedContentTypes)) {
      Object.entries(CONTENT_FEATURE_BY_TYPE).forEach(([type, featureKey]) => {
        seeded[featureKey] = selectedContentTypes.includes(type);
      });
    }

    this.features = seeded;
    this.isLoaded = true;
    this._saveToCache(this.features, this.customLimits);

    return { features: this.features, customLimits: this.customLimits };
  }

  /**
   * Check if a feature is enabled
   */
  isEnabled(featureKey) {
    return this.features[featureKey] === true;
  }

  /**
   * Get custom limit for a feature
   */
  getLimit(limitKey, defaultValue = null) {
    return this.customLimits[limitKey] ?? defaultValue;
  }

  /**
   * Get all features
   */
  getFeatures() {
    return this.features;
  }

  /**
   * Get all custom limits
   */
  getCustomLimits() {
    return this.customLimits;
  }

  /**
   * Reload features from backend
   */
  async reload() {
    return this.fetchUserFeatures();
  }
}

// Create singleton instance
const featureFlagService = new FeatureFlagService();

export default featureFlagService;
